/**
 * RESPONSIBILITY: which sections make up the About page, and in what order.
 *
 * The order follows the arc a first-time visitor needs: who we are, our own story — the
 * most specific, human thing on the page — then what we hold to, what we believe, and
 * who leads it. The story used to run last, after the tenets had already routed the
 * reader off to a ministry page; a page that explains itself only after sending the
 * visitor elsewhere has not explained itself in time.
 *
 * `#about/story` is the full history on a page of its own; About shows an opening and a way
 * through, since a story printed twice is a story corrected once.
 *
 * The statement of faith is ON this page, under the mission and vision — it was a page of
 * its own. `#about/believe`, which the menu's "What we believe" points at, still works: it
 * shows this page and scrolls to that band. Any `#about/<name>` does the same for a band
 * carrying `data-anchor="<name>"`, so a menu item can point at any region of the page.
 */
import { SectionName } from '@app/sections';
import { SectionUpdate } from '../cms';
import { useScrollToAnchor } from '../useScrollToAnchor';
import {
  Crumbs, AboutHeroBanner, AboutPurpose, AboutTenets, AboutFaith,
  AboutStory, AboutAnniversary, AboutStoryTeaser, AboutLeaderTeaser, AboutEstablished, Reveal, useWords,
} from '../site';

export function AboutPage({ path = [] }: { path?: readonly string[] }) {
  const w = useWords();
  // Before the early return below: a hook must run on every render or none.
  useScrollToAnchor(path[0] && path[0] !== 'story' ? path[0] : undefined);

  if (path[0] === 'story') {
    // The crumbs are the story's, drawn first; the anniversary band sits between them and
    // the story, above it, as R65 asks.
    return (
      <main>
        <SectionUpdate name={SectionName.AboutStory} pencil="none">
          {(c) => <Crumbs trail={[{ label: w.aboutLabel }, { label: c.heading }]} />}
        </SectionUpdate>
        <SectionUpdate name={SectionName.AboutAnniversary}>
          {(c) => <AboutAnniversary c={c} />}
        </SectionUpdate>
        <SectionUpdate name={SectionName.AboutStory}>
          {(c) => <AboutStory c={c} />}
        </SectionUpdate>
      </main>
    );
  }

  return (
    <main className="about-page-main">
      <Crumbs trail={[{ label: w.aboutLabel }]} />

      <SectionUpdate name={SectionName.AboutHero}>
        {(c) => <AboutHeroBanner c={c} />}
      </SectionUpdate>

      {/* After the banner, before the mission: when the church began, and who it is. */}
      <SectionUpdate name={SectionName.AboutEstablished}>
        {(c) => <Reveal><AboutEstablished c={c} /></Reveal>}
      </SectionUpdate>

      <SectionUpdate name={SectionName.AboutPurpose}>
        {(c) => <Reveal><AboutPurpose c={c} /></Reveal>}
      </SectionUpdate>

      {/* Straight after the mission and vision: what the church is for, then what it holds.
          Not inside a Reveal — #about/believe scrolls here, and a band that fades in only
          once it is in view would arrive blank under the reader's eyes. */}
      <SectionUpdate name={SectionName.AboutFaith}>
        {(c) => <AboutFaith c={c} />}
      </SectionUpdate>

      <SectionUpdate name={SectionName.AboutStory}>
        {(c) => <Reveal><AboutStoryTeaser c={c} /></Reveal>}
      </SectionUpdate>

      <SectionUpdate name={SectionName.AboutTenets}>
        {(c) => <Reveal><AboutTenets c={c} /></Reveal>}
      </SectionUpdate>

      {/* The pastor's note closes the page: a name and a face is a better last word than
          a link to a ministry. */}
      <SectionUpdate name={SectionName.LeadershipNote}>
        {(c) => <Reveal><AboutLeaderTeaser c={c} /></Reveal>}
      </SectionUpdate>
    </main>
  );
}
